import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { eventEmitter } from "../../../../shared/event";

type LeavePageWarningProps = {
  path: string;
  isFormDirty: boolean;
};
const dialogHeader = (
  <div>
    <i aria-hidden="true" className="pi pi-exclamation-triangle" style={{ marginRight: "8px" }}></i>
    <span>Warning</span>
  </div>
);
const LeavePageWarning: React.FC<LeavePageWarningProps> = ({ path, isFormDirty }) => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const confirmedNavigation = useRef(false);
  const hasPushedState = useRef(false);
  const pendingPath = useRef<string | null>(null);

  useEffect(() => {
    // Reset confirmation on location change
    confirmedNavigation.current = false;

    // If the form is not dirty anymore, allow re-pushing state again in the future
    if (!isFormDirty) {
      hasPushedState.current = false;
    }

    const handlePopState = () => {
      if (isFormDirty && !confirmedNavigation.current) {
        setVisible(true);

        // Prevent actual navigation by re-pushing current state
        window.history.pushState(null, "", location.pathname);
      } else {
        navigate(path);
      }
    };
    // Push a history state as soon as form becomes dirty
    if (isFormDirty && !hasPushedState.current) {
      window.history.pushState(null, "", location.pathname);
      hasPushedState.current = true;
    }

    const handleBeforeUnload = (event: any) => {
      if (isFormDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    const button = document.getElementById("backBtn");

    const handleClick = (e: any) => {
      if (isFormDirty && !confirmedNavigation.current) {
        e.preventDefault();
        e.stopPropagation();
        setVisible(true);
      }
    };

    // NEW: listen for navigation attempts from other components
    const onNavigateTab = (tabPath: string) => {
      if (isFormDirty && !confirmedNavigation.current) {
        setVisible(true);
        pendingPath.current = tabPath;
        // Prevent actual navigation by re-pushing current state
        window.history.pushState(null, "", tabPath);
      } else {
        navigate(tabPath);
      }
    };

    eventEmitter.on("navigateTab", onNavigateTab);

    window.addEventListener("beforeunload", handleBeforeUnload); // handle page refresh
    window.addEventListener("popstate", handlePopState); // handle back button

    if (button) {
      button.addEventListener("click", handleClick);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (button) {
        button.removeEventListener("click", handleClick);
      }
      eventEmitter.off("navigateTab", onNavigateTab);
    };
  }, [isFormDirty, location.pathname]);

  const handleLeavePage = () => {
    setVisible(false);
    confirmedNavigation.current = true;
    eventEmitter.emit("IsDirtyForm", false); // handle navbar
    navigate(pendingPath.current || path, { replace: true });
    pendingPath.current = null;
  };

  const handleStay = () => {
    setVisible(false);
    eventEmitter.emit("IsDirtyForm", true);
  };

  return LeaveDialog(visible, handleStay, handleLeavePage);
};

export default LeavePageWarning;

export function LeaveDialog(visible: boolean, handleStay: () => void, handleLeavePage: () => void): JSX.Element | null {
  if (!visible) return null;
  return (
    <Dialog header={dialogHeader} visible={visible} onHide={handleStay} modal style={{ width: "50vw" }} className="z-top modal-styles">
      <p className="p-4">{"You have unsaved changes that will be lost if you decide to continue. Are you sure you want to leave this page?"}</p>
      <div className="flex justify-center gap-2 border-t pt-5">
        <Button label="Leave this page" onClick={handleLeavePage} className="filled-round-button mr-3" />
        <Button label="Stay on this page" onClick={handleStay} className="outline-button " />
      </div>
    </Dialog>
  );
}
